import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Building2, Upload,
  BarChart3, Scale, ArrowRightLeft, Layers,
  Receipt, BookOpen, Users, PenLine,
  BookMarked, Package, FileText, ScrollText, ShoppingCart,
  BarChart2, Bot, TrendingUp, CreditCard,
  ChevronsLeft, ChevronRight, ShieldCheck, Landmark, Settings, Inbox, Banknote,
  ClipboardList, Home, MessageCircle, HelpCircle, DollarSign, FileX, CalendarClock,
} from 'lucide-react';
import type { Labels } from './Layout';
import type { LucideIcon } from 'lucide-react';
import { useCompanyContext } from '../../context/CompanyContext';
import { supabase } from '../../services/supabaseClient';
import BrandLogo from '../shared/BrandLogo';

const APP_NAME = import.meta.env.VITE_APP_NAME ?? 'TryQuiPus';
const _dotIdx = APP_NAME.indexOf('.');
const APP_NAME_PRE = _dotIdx >= 0 ? APP_NAME.slice(0, _dotIdx) : APP_NAME;
const APP_NAME_SUF = _dotIdx >= 0 ? APP_NAME.slice(_dotIdx) : '';
import { useLang } from '../../context/LangContext';
import { useRoleContext } from '../../context/RoleContext';
import { hasCompanyKey } from '../../services/EncryptionService';
import { t } from '../../i18n/dictionary';
import { useUserRole } from '../../context/UserRoleContext';
import { getInitialOpenSections, persistOpenSections, getSectionForRoute, type SidebarSectionId } from './sidebarState';


interface NavItem {
  labelKey: keyof Labels;
  icon: LucideIcon;
  to?: string;
  badge?: string;
  availableIn?: string[];  // si presente: visible solo si jurisdiction ∈ availableIn
  indent?: boolean;        // sub-item indentado
}

interface Section {
  titleKey: keyof Labels;
  /** Punto de color tenue de la sección (opción C): token, nunca verde/rojo/ámbar. */
  dot: string;
  id: SidebarSectionId;
  items: NavItem[];
}

const DOT_NEUTRAL = 'var(--sidebar-muted)';

const sections: Section[] = [
  {
    titleKey: 'dashboardEmpresarial',    // → 'INICIO'
    dot: 'var(--sidebar-dot-inicio)',
    id: 'inicio',
    items: [
      { to: '/',          labelKey: 'miNegocioItem', icon: Home      },
      { to: '/dashboard', labelKey: 'resultados',    icon: BarChart3 },
    ],
  },
  {
    titleKey: 'motorProcesamiento',      // → 'OPERACIÓN DIARIA'
    dot: 'var(--sidebar-dot-operacion)',
    id: 'operacion',
    items: [
      { to: '/upload',          labelKey: 'cargar',               icon: Upload   },
      { to: '/bank-inbox',      labelKey: 'bandejaBancaria',      icon: Landmark },
      { to: '/inbox',           labelKey: 'bandejaClasificacion', icon: Inbox    },
      { to: '/cargas-fallidas', labelKey: 'inboxFallidos',        icon: FileX    },
      { to: '/loans',           labelKey: 'prestamos',            icon: DollarSign },
      { to: '/asientos-automaticos', labelKey: 'asientosAutomaticos', icon: CalendarClock },
      { to: '/agentes',         labelKey: 'agentes',              icon: Bot      },
    ],
  },
  {
    titleKey: 'construccion',            // → 'LIBROS CONTABLES'
    dot: 'var(--sidebar-dot-libros)',
    id: 'libros',
    items: [
      { to: '/journals',     labelKey: 'journals',    icon: PenLine    },
      { to: '/libro-diario', labelKey: 'libroDiario', icon: ScrollText },
      { to: '/libro-mayor',  labelKey: 'libroMayor',  icon: BookMarked },
      { to: '/sumas-saldos', labelKey: 'sumasSaldos', icon: Scale      },
      {                      labelKey: 'kardex',      icon: Package,  badge: 'sidebar.milestone4' },
    ],
  },
  {
    titleKey: 'inteligenciaFinanciera',  // → 'REPORTES FINANCIEROS'
    dot: 'var(--sidebar-dot-reportes)',
    id: 'reportes',
    items: [
      { to: '/cierre',  labelKey: 'cierre',  icon: Layers         },
      { to: '/balance', labelKey: 'balance', icon: Scale          },
      { to: '/pyg',     labelKey: 'pyg',     icon: FileText       },
      { to: '/flujo',   labelKey: 'flujo',   icon: ArrowRightLeft },
      { to: '/cxp',     labelKey: 'cxp',     icon: CreditCard     },
      { to: '/cxc',     labelKey: 'cxc',     icon: Users          },
    ],
  },
  {
    titleKey: 'cumplimientoTributario',
    dot: DOT_NEUTRAL,
    id: 'tributario',
    items: [
      { to: '/tributaria',    labelKey: 'panel',            icon: Receipt       },
      { to: '/compras',       labelKey: 'compras',          icon: ShoppingCart  },
      { to: '/ventas',        labelKey: 'ventas',           icon: TrendingUp    },
      { to: '/impuestos',     labelKey: 'impuestosIgv',     icon: BarChart2     },
      { to: '/declaraciones', labelKey: 'pdt621',           icon: ClipboardList },
      { to: '/detracciones',  labelKey: 'detracciones',     icon: Banknote,    availableIn: ['PE'] },
      { to: '/maestro-fiscal',labelKey: 'categorizador',    icon: Bot           },
      // Sub-header "Libros Electrónicos" (label sin badge, sub-items indentados)
      {                       labelKey: 'librosElectronicos', icon: ScrollText  },
      { to: '/libros-pe/diario',               labelKey: 'pleDiario51',   icon: ScrollText, indent: true },
      { to: '/libros-pe/libro-mayor',          labelKey: 'pleMayor61',    icon: BookMarked, indent: true },
      { to: '/libros-pe/caja-bancos',          labelKey: 'pleCajaBancos', icon: Landmark,   indent: true },
      { to: '/libros-pe/balance-comprobacion', labelKey: 'pleBalance317', icon: Scale,      indent: true },
    ],
  },
  {
    titleKey: 'guiaBiblioteca',          // → 'BIBLIOTECA'
    dot: DOT_NEUTRAL,
    id: 'asistente',
    items: [
      { to: '/biblioteca',        labelKey: 'ayuda',         icon: BookOpen },
    ],
  },
  {
    titleKey: 'configuracionSection',    // → 'CONFIGURACIÓN'
    dot: DOT_NEUTRAL,
    id: 'configuracion',
    items: [
      { to: '/perfil-empresa',  labelKey: 'perfilEmpresa',    icon: Building2   },
      { to: '/bank-accounts',   labelKey: 'cuentasBancarias', icon: Landmark    },
      { to: '/team-settings',   labelKey: 'teamSettings',     icon: Users       },
      { to: '/audit',           labelKey: 'auditoria',        icon: ShieldCheck },
      { to: '/settings',        labelKey: 'settings',         icon: Settings    },
    ],
  },
];

interface SidebarProps {
  labels: Labels;
  isMobileOpen: boolean;
  onMobileClose: () => void;
}

/** Fila de navegación del menú (opción C): 32 px, radio 8, ícono 16, activo = fondo + acento. */
function NavRow({ to, icon: Icon, label, active, collapsed, indent, end, trailing }: {
  to: string; icon: LucideIcon; label: string; active: boolean; collapsed: boolean;
  indent?: boolean; end?: boolean; trailing?: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      title={label}
      aria-label={collapsed ? label : undefined}
      aria-current={active ? 'page' : undefined}
      className={`fx sh-item${active ? ' is-active' : ''}${collapsed ? ' is-collapsed' : ''}${indent && !collapsed ? ' is-indent' : ''}`}
    >
      <Icon size={16} aria-hidden="true" />
      {!collapsed && <span className="flex-1">{label}</span>}
      {!collapsed && trailing}
    </NavLink>
  );
}

function SectionTitle({ dot, children }: { dot: string; children: React.ReactNode }) {
  return (
    <span className="sh-title-text">
      <span className="sh-dot" style={{ ['--dot' as string]: dot }} aria-hidden="true" />
      {children}
    </span>
  );
}

function CountBadge({ value, tone }: { value: number; tone: 'brand' | 'negative' }) {
  return (
    <span
      className="tabular-nums min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1"
      style={{
        fontSize: 'var(--type-caption-size)', fontWeight: 700,
        background: tone === 'brand' ? 'var(--brand)' : 'var(--negative)',
        color: 'var(--on-brand)',
      }}
    >
      {value > 99 ? '99+' : value}
    </span>
  );
}

export default function Sidebar({ labels, isMobileOpen, onMobileClose }: SidebarProps) {
  const location = useLocation();
  const { activeCompany, activeFirmId, filterPeriodo, companiesCount, companyLimit } = useCompanyContext();
  const { lang } = useLang();
  const { isCollapsed, toggleCollapsed } = useRoleContext();

  // Per-section open state — only active section open by default
  const [openSections, setOpenSections] = useState<Set<SidebarSectionId>>(
    () => getInitialOpenSections(location.pathname)
  );

  useEffect(() => {
    const target = getSectionForRoute(location.pathname);
    setOpenSections(prev => {
      if (prev.has(target)) return prev;
      const next = new Set(prev);
      next.add(target);
      return next;
    });
  }, [location.pathname]);

  // Pending classification badge — lightweight HEAD count query
  const [inboxPendingCount, setInboxPendingCount] = useState(0);
  useEffect(() => {
    if (!activeCompany || !activeFirmId) return;
    const periodo = filterPeriodo || activeCompany.periodo || '';
    if (!periodo) return;
    let mounted = true;
    supabase
      .from('transactions')
      .select('db_id', { count: 'exact', head: true })
      .eq('firm_id',    activeFirmId)
      .eq('company_id', activeCompany.companyId)
      .eq('periodo',    periodo)
      .is('tipo_documento', null)
      .not('status', 'in', '("approved","annulled")')
      .then(({ count }) => { if (mounted) setInboxPendingCount(count ?? 0); });
    return () => { mounted = false; };
  }, [activeCompany, activeFirmId, filterPeriodo]);

  // Failed ingestion badge — unresolved parse errors
  const [failedIngestionCount, setFailedIngestionCount] = useState(0);
  useEffect(() => {
    if (!activeCompany || !activeFirmId) return;
    let mounted = true;
    supabase
      .from('failed_ingestion_logs')
      .select('id', { count: 'exact', head: true })
      .eq('firm_id',    activeFirmId)
      .eq('company_id', activeCompany.companyId)
      .is('resolved_at', null)
      .then(({ count }) => { if (mounted) setFailedIngestionCount(count ?? 0); });
    return () => { mounted = false; };
  }, [activeCompany, activeFirmId]);

  function toggleSection(id: SidebarSectionId) {
    setOpenSections(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      persistOpenSections(next);
      return next;
    });
  }

  const ALL_SECTIONS = new Set<SidebarSectionId>([
    'inicio', 'operacion', 'libros', 'reportes', 'tributario', 'asistente', 'configuracion',
  ]);

  function expandAll() {
    setOpenSections(new Set(ALL_SECTIONS));
    persistOpenSections(new Set(ALL_SECTIONS));
  }

  function collapseAll() {
    const next = new Set<SidebarSectionId>([getSectionForRoute(location.pathname)]);
    setOpenSections(next);
    persistOpenSections(next);
  }

  const { viewMode: effectiveRole } = useUserRole();

  // Routes hidden for emprendedor role — only basic dashboard + financial reports visible
  const EMPRENDEDOR_HIDDEN = new Set([
    // Operación Diaria (advanced — accountant-only)
    '/bank-accounts', '/bank-inbox', '/inbox', '/agentes',
    // Libros Contables (accounting books — accountant-only)
    '/journals', '/libro-diario', '/libro-mayor', '/sumas-saldos',
    // Cumplimiento Tributario (detailed compliance — accountant-only)
    '/compras', '/ventas', '/maestro-fiscal', '/audit',
    '/cargas-fallidas',
    '/libros-pe/diario', '/libros-pe/libro-mayor',
    '/libros-pe/caja-bancos', '/libros-pe/balance-comprobacion',
  ]);

  function isItemVisible(to: string | undefined, availableIn: string[] | undefined): boolean {
    if (availableIn) {
      const jurisdiction = activeCompany?.jurisdiction ?? 'PE';
      if (!availableIn.includes(jurisdiction)) return false;
    }
    if (effectiveRole === 'emprendedor') {
      return !to || !EMPRENDEDOR_HIDDEN.has(to);
    }
    return true;
  }


  return (
    <>
      {/* Mobile backdrop */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}
    <aside
      className={`sh-side force-dark w-[240px] ${isCollapsed ? 'md:w-16' : ''} h-screen flex flex-col fixed left-0 top-0 z-50
                  transition-[width,transform] duration-300 ease-in-out
                  ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
    >
      {/* Header — marca + contraer/expandir menú */}
      <div
        className={`flex items-center ${isCollapsed ? 'px-3 py-3 justify-center' : 'px-5 pt-5 pb-4 justify-between'}`}
        style={{ borderBottom: '1px solid var(--sidebar-edge)' }}
      >
        {isCollapsed ? (
          <button
            onClick={toggleCollapsed}
            title={t('sidebar.expand', lang)}
            aria-label={t('sidebar.expand', lang)}
            className="fx flex items-center justify-center flex-shrink-0"
          >
            <BrandLogo size={36} forceLight />
          </button>
        ) : (
          <div>
            <h1 className="brand-logo">
              {APP_NAME_PRE}{APP_NAME_SUF && <span className="brand-logo-suffix">{APP_NAME_SUF}</span>}<span className="brand-logo-tm">™</span>
            </h1>
            <p className="sh-brand-tag mt-0.5">{t('sidebar.tagline', lang)}</p>
          </div>
        )}
        {!isCollapsed && (
          <button
            onClick={toggleCollapsed}
            className="fx sh-item is-collapsed flex-shrink-0 ml-2"
            style={{ width: 32 }}
            title={t('sidebar.collapse', lang)}
            aria-label={t('sidebar.collapse', lang)}
          >
            <ChevronsLeft size={16} aria-hidden="true" />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav
        aria-label={t('shell.mainMenu', lang)}
        className={`flex-1 ${isCollapsed ? 'px-1.5' : 'px-3'} pt-4 pb-8 space-y-4 overflow-y-auto sh-nav`}
        onClick={onMobileClose}
      >

        {/* Despacho Contable — contador only (gated by persisted role, not UI mode toggle) */}
        {effectiveRole === 'contador' && <div>
            {!isCollapsed && (
              <div className="mb-1">
                <div className="sh-title"><SectionTitle dot={DOT_NEUTRAL}>{labels.despacho}</SectionTitle></div>
                {companyLimit !== null && (
                  <p className="sh-brand-tag px-2">{companiesCount} de {companyLimit} empresas</p>
                )}
              </div>
            )}
            <div className="space-y-0.5">
              {([
                { to: '/contador',          icon: Users,     labelKey: 'consolaContador' },
                { to: '/contador/clientes', icon: Building2, labelKey: 'clientes'        },
              ] as const).map(({ to, icon, labelKey }) => (
                <NavRow
                  key={to}
                  to={to}
                  icon={icon}
                  label={labels[labelKey]}
                  active={location.pathname === to}
                  collapsed={isCollapsed}
                />
              ))}
            </div>
          </div>}

        {/* ── Emprendedor nav — completely different experience ── */}
        {effectiveRole === 'emprendedor' && (() => {
          const empSections = [
            {
              id: 'mi-negocio',
              titleKey: 'miNegocio' as const,
              dot: 'var(--sidebar-dot-inicio)',
              items: [
                { to: '/',           labelKey: 'resumen'     as const, icon: Home       },
                { to: '/upload',     labelKey: 'misFacturas'  as const, icon: Upload     },
                { to: '/cxp',        labelKey: 'misPagos'     as const, icon: DollarSign },
                { to: '/balance',    labelKey: 'misReportes'  as const, icon: BarChart3  },
                { to: '/impuestos',  labelKey: 'miSunat'      as const, icon: Receipt    },
              ],
            },
            {
              id: 'soporte',
              titleKey: 'soporte' as const,
              dot: DOT_NEUTRAL,
              items: [
                { to: '/biblioteca', labelKey: 'ayudaEmprendedor' as const, icon: HelpCircle    },
              ],
            },
          ] as const;

          return empSections.map(sec => (
            <div key={sec.id}>
              {!isCollapsed
                ? <div className="sh-title mb-1"><SectionTitle dot={sec.dot}>{labels[sec.titleKey]}</SectionTitle></div>
                : <div className="mx-2 mb-2" style={{ borderTop: '1px solid var(--sidebar-edge)' }} />}
              <div className="space-y-0.5">
                {sec.items.map(({ to, labelKey, icon }) => (
                  <NavRow
                    key={to}
                    to={to}
                    icon={icon}
                    label={labels[labelKey]}
                    active={location.pathname === to || location.pathname.startsWith(to + '/')}
                    collapsed={isCollapsed}
                  />
                ))}

                {/* Hablar con mi contador — opens WhatsApp del contador asignado a la empresa activa */}
                {!isCollapsed && sec.id === 'soporte' && (
                  activeCompany?.contadorWhatsapp ? (
                    <button
                      onClick={() => {
                        const numero = activeCompany.contadorWhatsapp!.replace(/[^0-9]/g, '');
                        window.open(`https://wa.me/${numero}`, '_blank', 'noopener,noreferrer');
                      }}
                      className="fx sh-item"
                    >
                      <MessageCircle size={16} aria-hidden="true" />
                      <span className="flex-1 text-left">{labels.hablarContador}</span>
                    </button>
                  ) : (
                    <NavLink to="/setup" className="fx sh-item">
                      <MessageCircle size={16} aria-hidden="true" />
                      <span className="flex-1 text-left">{t('shell.setupWhatsapp', lang)}</span>
                    </NavLink>
                  )
                )}
              </div>
            </div>
          ));
        })()}

        {/* ── Contador / Admin nav — full accounting sections ── */}
        {effectiveRole !== 'emprendedor' && sections.map((section) => {
          const isSectionOpen = openSections.has(section.id);

          return (
            <div key={section.id}>
              {/* Section header — clickable when expanded */}
              {!isCollapsed ? (
                <button
                  onClick={(e) => { e.stopPropagation(); toggleSection(section.id); }}
                  className="sh-title mb-1"
                  aria-expanded={isSectionOpen}
                >
                  <SectionTitle dot={section.dot}>{labels[section.titleKey]}</SectionTitle>
                  <ChevronRight
                    size={12}
                    className="flex-shrink-0"
                    aria-hidden="true"
                    style={{
                      transition: 'transform 200ms var(--ease-out)',
                      transform: isSectionOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                    }}
                  />
                </button>
              ) : (
                /* Collapsed sidebar: thin divider */
                <div className="mx-2 mb-2" style={{ borderTop: '1px solid var(--sidebar-edge)' }} />
              )}

              {/* Items — animated max-height, always in DOM */}
              <div
                className="sb-section-items space-y-0.5"
                style={{ maxHeight: (isSectionOpen || isCollapsed) ? '1200px' : '0' }}
              >
                  {section.items.filter(({ to, availableIn }) => isItemVisible(to, availableIn)).map(({ to, labelKey, icon: Icon, badge, indent }) => {
                    if (!to) {
                      if (isCollapsed) return null;
                      return (
                        <span key={String(labelKey)} className="sh-item is-disabled">
                          <Icon size={16} aria-hidden="true" />
                          <span className="flex-1">{labels[labelKey]}</span>
                          {badge && (
                            <span className="sh-brand-tag font-semibold uppercase tracking-wider">
                              {t(badge, lang)}
                            </span>
                          )}
                        </span>
                      );
                    }

                    const isActive = to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

                    return (
                      <NavRow
                        key={String(labelKey)}
                        to={to}
                        end={to === '/'}
                        icon={Icon}
                        label={labels[labelKey]}
                        active={isActive}
                        collapsed={isCollapsed}
                        indent={indent}
                        trailing={
                          to === '/inbox' && inboxPendingCount > 0
                            ? <CountBadge value={inboxPendingCount} tone="brand" />
                            : to === '/cargas-fallidas' && failedIngestionCount > 0
                              ? <CountBadge value={failedIngestionCount} tone="negative" />
                              : undefined
                        }
                      />
                    );
                  })}
              </div>
            </div>
          );
        })}

      </nav>

      {/* DEV-only: Email Preview link */}
      {import.meta.env.DEV && !isCollapsed && (
        <div className="px-3 pb-2">
          <div className="mb-1" style={{ borderTop: '1px solid var(--sidebar-edge)' }} />
          <NavLink
            to="/email-preview"
            className={({ isActive }) => `fx sh-item${isActive ? ' is-active' : ''}`}
          >
            <span className="sh-brand-tag font-semibold uppercase tracking-wider">DEV</span>
            <span>{labels.emailPreview}</span>
          </NavLink>
        </div>
      )}

      {/* Encryption badge */}
      {!isCollapsed && activeCompany && hasCompanyKey(activeCompany.companyId) && (
        <div className="flex items-center gap-1.5 px-5 py-2 font-medium" style={{ fontSize: 'var(--type-caption-size)', color: 'var(--sidebar-active)' }}>
          <span aria-hidden="true">🔐</span>
          <span>{t('sidebar.encrypted', lang)}</span>
        </div>
      )}

      {/* Footer */}
      {!isCollapsed && (
        <div className="px-5 py-3" style={{ borderTop: '1px solid var(--sidebar-edge)' }}>
          <div className="flex items-center gap-2 mb-2">
            <button onClick={expandAll} className="sh-link">
              {t('shell.expandAll', lang)}
            </button>
            <span className="sh-brand-tag" aria-hidden="true">·</span>
            <button onClick={collapseAll} className="sh-link">
              {t('shell.collapseAll', lang)}
            </button>
          </div>
          <p className="sh-brand-tag">
            {t('footer.version', lang)}
          </p>
        </div>
      )}
    </aside>
    </>
  );
}
